import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import streamingClientsService from '../services/streamingClientsService';
import ConfirmModal from './shared/ConfirmModal';
import StreamingClientPanel, { stateOf, KIND_LABEL } from './streaming/StreamingClientPanel';
import './OxiDequeueAdmin.css';
import './AdminSyncConnectQueries.css';
import './streaming/StreamingClients.css';

// ADMIN › INFRA › STREAMING CLIENTS — every hub-run streaming consumer (containers/ohip-events on the code-eks cluster):
// each pipeline implementation and sync project whose source system has uses_streaming_client = 'Y', with what is
// deployed for it right now, read live from Kubernetes. Deploy / redeploy / stop from here, and read or tail a client's
// container log. "Orphan" = a Deployment whose implementation or project is gone; "Other" = one Terraform manages.

const AUTO_REFRESH_MS = 3000;
const errText = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message;
const when = (v) => (v ? new Date(v).toLocaleString() : '—');

const linkFor = (r) => {
    if (r.kind === 'pipeline' && r.templateId) return `/pipeline-templates/${r.templateId}/edit?from=pipelines&tab=implementations`;
    if (r.kind === 'sync' && r.refId) return `/sync/${r.refId}`;
    return null;
};

export default function AdminStreamingClients() {
    const [data, setData] = useState({ clients: [], cluster: null, checkedAt: null });
    const [filters, setFilters] = useState({ kind: '', state: '', search: '' });
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openKey, setOpenKey] = useState(null);
    const [detail, setDetail] = useState(null);             // the open client's own status, with `resolved` and `missing`
    const [confirmStop, setConfirmStop] = useState(null);   // { key, resolve }
    const inFlight = useRef(false);

    const load = useCallback(async ({ quiet } = {}) => {
        if (inFlight.current) return;
        inFlight.current = true;
        if (!quiet) setLoading(true);
        try {
            setData((await streamingClientsService.list()).data);
            setError(null);
        } catch (e) {
            setError(errText(e));
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // AUTO REFRESH every 3 seconds while ticked — paused while a client is open, so its form and log are not disturbed.
    useEffect(() => {
        if (!autoRefresh || openKey) return undefined;
        const id = setInterval(() => load({ quiet: true }), AUTO_REFRESH_MS);
        return () => clearInterval(id);
    }, [autoRefresh, openKey, load]);

    const rows = useMemo(() => {
        const q = filters.search.trim().toLowerCase();
        return (data.clients || []).filter(r => {
            if (filters.kind && r.kind !== filters.kind) return false;
            const st = r.deployment ? r.deployment.state : 'not-deployed';
            if (filters.state && st !== filters.state) return false;
            if (q && ![r.key, r.name, r.container, r.hotelName, r.chainName, r.templateName, r.systemName, r.targetSystemName, r.credentialName]
                .some(v => v && String(v).toLowerCase().includes(q))) return false;
            return true;
        });
    }, [data.clients, filters]);

    const counts = useMemo(() => {
        const c = { running: 0, failing: 0 };
        for (const r of data.clients || []) if (r.deployment && c[r.deployment.state] !== undefined) c[r.deployment.state] += 1;
        return c;
    }, [data.clients]);

    const open = (data.clients || []).find(r => r.key === openKey) || null;
    // The panel's status shape, from the list row.
    const statusOf = (r) => ({
        key: r.key,
        client: r.kind === 'pipeline' || r.kind === 'sync' ? r : null,
        deployment: r.deployment,
        clusterError: data.cluster && data.cluster.error,
    });

    // OPENING A CLIENT reads it on its own: the list carries no credential details (that would decrypt every client's
    // credential on every 3-second refresh), the single read says which credential and values a deploy will use.
    const openClient = async (key) => {
        setOpenKey(key);
        setDetail(null);
        try {
            setDetail((await streamingClientsService.get(key)).data);
        } catch (e) {
            setError(errText(e));
        }
    };
    const closeClient = () => { setOpenKey(null); setDetail(null); };

    const deploy = async (key, settings) => {
        setDetail((await streamingClientsService.deploy(key, settings)).data);
        await load({ quiet: true });
    };

    // Stop is confirmed in a ConfirmModal (never window.confirm); the panel waits on the answer.
    const stop = (key) => new Promise((resolve, reject) => setConfirmStop({ key, resolve, reject }));
    const confirmStopNow = async () => {
        const c = confirmStop;
        setConfirmStop(null);
        try {
            await streamingClientsService.stop(c.key);
            await load({ quiet: true });
            if (openKey === c.key) {
                try { setDetail((await streamingClientsService.get(c.key)).data); } catch (e) { /* the list still shows it */ }
            }
            c.resolve();
        } catch (e) {
            c.reject(e);
        }
    };

    const cluster = data.cluster;

    return (
        <div className="acp-page">
            <div className="acp-header">
                <div>
                    <h1 className="acp-title">Streaming Clients</h1>
                    <p className="acp-subtitle">
                        The streaming consumers the hub runs on EKS, one per pipeline implementation or sync project whose
                        source system uses a streaming client. Status is read live from the cluster.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
                        Auto refresh (3s)
                    </label>
                    <button className="acp-btn acp-btn-secondary" onClick={() => load()} disabled={loading}>Refresh</button>
                </div>
            </div>

            {error && <div className="os-msg os-msg-error" onClick={() => setError(null)}>{error}</div>}
            {cluster && !cluster.reachable && (
                <div className="os-msg os-msg-error">Cluster {cluster.name} could not be read: {cluster.error}. Deployment status is unknown.</div>
            )}

            <div className="acp-card">
                <div className="acp-card-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span className="acp-card-title">
                        {(data.clients || []).length} client{(data.clients || []).length === 1 ? '' : 's'} · {counts.running} running
                        {counts.failing ? ` · ${counts.failing} failing` : ''}
                    </span>
                    <select className="odq-filter-select" value={filters.kind} onChange={e => setFilters(f => ({ ...f, kind: e.target.value }))}>
                        <option value="">All kinds</option>
                        {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <select className="odq-filter-select" value={filters.state} onChange={e => setFilters(f => ({ ...f, state: e.target.value }))}>
                        <option value="">All states</option>
                        <option value="running">Running</option>
                        <option value="starting">Starting</option>
                        <option value="failing">Failing</option>
                        <option value="not-deployed">Not deployed</option>
                    </select>
                    <input className="odq-filter-select" placeholder="Search name, hotel, chain…" value={filters.search}
                           onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
                    {data.checkedAt && <span className="scq-meta" style={{ marginLeft: 'auto' }}>Updated {new Date(data.checkedAt).toLocaleTimeString()}</span>}
                </div>
                {loading && !(data.clients || []).length ? <div className="acp-empty">Loading…</div>
                    : rows.length === 0 ? <div className="acp-empty">No streaming clients{(data.clients || []).length ? ' match these filters' : ' — no implementation or sync project has a source that uses a streaming client'}.</div>
                    : (
                    <table className="acp-table">
                        <thead>
                            <tr>
                                <th>Kind</th><th>Name</th><th>Property</th><th>Source → target / container</th><th>Credential</th>
                                <th>Status</th><th>Pod</th><th>Deployed</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const st = stateOf(r);
                                const link = linkFor(r);
                                return (
                                    <tr key={r.key}>
                                        <td>{KIND_LABEL[r.kind] || r.kind}<div className="scq-meta scq-mono">{r.key}</div></td>
                                        <td>{link ? <Link to={link}>{r.name}</Link> : r.name}
                                            <div className="scq-meta">{r.kind === 'pipeline' ? r.templateName : r.kind === 'sync' ? r.projectType : ''}
                                                {r.refStatus ? ` · ${r.refStatus}` : ''}</div></td>
                                        <td>{r.hotelName || '—'}<div className="scq-meta">{r.chainName || ''}</div></td>
                                        <td>{r.systemName || '—'}{r.targetSystemName ? ` → ${r.targetSystemName}` : ''}
                                            {(r.container || (r.deployment && r.deployment.container)) &&
                                                <div className="scq-meta scq-mono">{r.container || r.deployment.container}</div>}</td>
                                        <td>{r.credentialName || (r.connectorId ? `Connector ${r.connectorId}` : '—')}</td>
                                        <td><span className={`scq-status scq-status-${st.cls}`}>{st.label}</span>
                                            {r.deployment && r.deployment.reason && <div className="scq-meta">{r.deployment.reason}</div>}
                                            {r.settings && r.settings.lastError && <div className="scq-meta" style={{ color: '#b91c1c' }}>Last {r.settings.lastAction} failed</div>}</td>
                                        <td>{r.deployment && r.deployment.pod
                                            ? <>{r.deployment.restarts} restart{r.deployment.restarts === 1 ? '' : 's'}<div className="scq-meta">{r.deployment.podPhase}</div></>
                                            : '—'}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{r.deployment ? when(r.deployment.deployedAt) : '—'}</td>
                                        <td><button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => openClient(r.key)}>Open</button></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {open && (
                <div className="odq-modal-overlay" onClick={closeClient}>
                    <div className="odq-modal stc-modal" onClick={e => e.stopPropagation()}>
                        <div className="odq-modal-head">
                            <span>{KIND_LABEL[open.kind] || open.kind} · {open.name} <span className="scq-meta scq-mono">{open.key}</span></span>
                            <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={closeClient}>Close</button>
                        </div>
                        <div className="odq-modal-body">
                            <StreamingClientPanel
                                status={detail && detail.key === open.key ? detail : statusOf(open)}
                                onDeploy={(settings) => deploy(open.key, settings)}
                                onStop={() => stop(open.key)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {confirmStop && (
                <ConfirmModal
                    title="Stop streaming client"
                    message={`Stop ${confirmStop.key}? Its Deployment is deleted and the consumer disconnects from OHIP. Events published while it is stopped are not replayed when it is deployed again.`}
                    confirmLabel="Stop"
                    danger
                    onConfirm={confirmStopNow}
                    onCancel={() => { const c = confirmStop; setConfirmStop(null); c.resolve(); }}
                />
            )}
        </div>
    );
}
